import React from "react"
import {Col, Row} from "antd"
import styles from "./FinAidPackageInfo.module.css"
import {Auth} from "types/auth"
import {useTranslation} from "react-i18next"
import {FinAidOffer} from "types/fin-aid/fin-aid"
import {finAidService} from "services"
import cx from "classnames"
import {Icon} from "components"
import {useModel, useVisible} from "hooks"
import {Settings} from "types/settings"
import EditableStudentCustomFields from "components/EditableStudentCustomFields"
import {checkPermission} from "helpers"
import {Permissions} from "types/permission"
import {EditableFinAidField} from "./EditableFinAidField"

type Props = {
    student?: Auth.DepartmentStudent
    isPanel?: boolean
}

export function FinAidPackageInfo({student, isPanel = false}: Props) {
    const {t} = useTranslation(["financialAid"])
    const model = useModel()
    const detailVisible = useVisible(true)
    const canEdit = checkPermission(
        {staff: [Permissions.Staff.FinancialAid.Students.FinancialAidStudentDetail.Edit]},
        model
    )
    const [finAidOffer, setFinAidOffer] = React.useState<FinAidOffer | undefined>()

    React.useEffect(() => {
        ;(async function loadLatestFinAidOffer() {
            if (!student?.profileId) return
            setFinAidOffer(
                await finAidService.getFinAidLatestOffer({
                    studentProfileId: student.profileId,
                    linkedPackage: true
                })
            )
        })()
    }, [student?.profileId])

    const awardYear = finAidOffer?.awardYear ? `${finAidOffer.awardYear} - ${finAidOffer.awardYear + 1}` : "-"

    const dependencyStatus =
        student?.dependencyStatus || finAidOffer?.finAidPackage?.dependencyStatus
            ? t(
                  `financialAidPackage.dependencyStatuses.${
                      student?.dependencyStatus || finAidOffer?.finAidPackage?.dependencyStatus
                  }`
              )
            : "-"

    const payingMethods = student?.payingMethods?.map((method) => method.name).join(", ") || "-"

    const finAidStatus = finAidOffer?.status ? t(`finAidOffer.status.${finAidOffer?.status}`) : "-"

    const isir = finAidOffer?.finAidPackage?.isir || "-"

    if (isPanel) {
        return (
            <div className={styles.panelWrap}>
                <div className={styles.headingWrap}>
                    <div className={styles.headingTitle}>Financial Aid Details</div>
                    <button
                        className={cx(styles.collapseButton, {[styles.collapsed]: detailVisible.isVisible})}
                        onClick={detailVisible.toggle}>
                        <Icon icon="ARROW_DOWN" color="#666666" className={styles.headingIcon} />
                    </button>
                </div>

                {detailVisible.isVisible && student && (
                    <>
                        <EditableFinAidField
                            field="dependencyStatus"
                            student={student}
                            readOnly={!canEdit}
                            fallbackDependencyStatus={finAidOffer?.finAidPackage?.dependencyStatus}
                        />
                        <EditableFinAidField field="numberOfDependents" student={student} readOnly={!canEdit} />
                        <EditableFinAidField field="payingMethods" student={student} readOnly={!canEdit} />
                        <EditableFinAidField field="housingPlan" student={student} readOnly={!canEdit} />
                        <EditableFinAidField
                            field="awardYear"
                            student={student}
                            finAidOffer={finAidOffer}
                            onOfferUpdated={setFinAidOffer}
                            readOnly={!canEdit}
                        />
                        <EditableFinAidField
                            field="packageStatus"
                            student={student}
                            finAidOffer={finAidOffer}
                            onOfferUpdated={setFinAidOffer}
                            readOnly={!canEdit}
                        />
                        <EditableFinAidField
                            field="isir"
                            student={student}
                            finAidOffer={finAidOffer}
                            onOfferUpdated={setFinAidOffer}
                            readOnly={!canEdit}
                        />
                        <EditableStudentCustomFields
                            student={student}
                            module={Settings.CustomFieldModule.FinancialAid}
                        />
                    </>
                )}
            </div>
        )
    }

    return (
        <div className={styles.finAidInfo}>
            <Row gutter={[8, 8]}>
                <Col span={12}>
                    <div className={styles.infoSection}>
                        <div className={styles.infoLabel}>Current Award Year</div>
                        <div className={styles.infoValue} title={awardYear}>
                            {awardYear}
                        </div>
                    </div>
                </Col>
                <Col span={12}>
                    <div className={styles.infoSection}>
                        <div className={styles.infoLabel}>Dependency Status</div>
                        <div className={styles.infoValue} title={dependencyStatus}>
                            {dependencyStatus}
                        </div>
                    </div>
                </Col>
                <Col span={12}>
                    <div className={styles.infoSection}>
                        <div className={styles.infoLabel}>Method of paying for College</div>
                        <div className={styles.infoValue} title={payingMethods}>
                            {payingMethods}
                        </div>
                    </div>
                </Col>
                <Col span={12}>
                    <div className={styles.infoSection}>
                        <div className={styles.infoLabel}>Financial Aid Package Status</div>
                        <div className={styles.infoValue} title={finAidStatus}>
                            {finAidStatus}
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    )
}
